import type {
  Dispatch,
  PointerEvent as ReactPointerEvent,
  PropsWithChildren,
  ReactNode,
  SetStateAction,
} from "react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import { DocumentViewportContext } from "@/features/document-viewport/context";
import { ImageViewport } from "@/features/document-viewport/image-viewport";
import {
  DocumentViewportPageSidebar,
  PAGE_SIDEBAR_LEFT_INSET,
} from "@/features/document-viewport/page-sidebar";
import { PdfViewport } from "@/features/document-viewport/pdf-viewport";
import type {
  DocumentSource,
  PageMetrics,
  ViewportCamera,
  ViewportPadding,
  ViewportPoint,
  ViewportRenderState,
  ViewportRect,
  ViewportSize,
} from "@/features/document-viewport/types";
import {
  clampPanToDocumentBounds,
  clamp,
  getDefaultViewportPadding,
  getDocumentFrame,
  getFitPageZoom,
  MAX_VIEWPORT_ZOOM,
  pageRectToScreenRect,
  pageToScreenPoint,
  getRenderScaleForZoom,
  screenRectToPageRect,
  screenToPagePoint,
} from "@/features/document-viewport/utils";
import {
  isSpaceKey,
  isSpacePanContextActive,
  shouldIgnoreSpacePanTarget,
} from "@/lib/keyboard-targets";

interface DocumentViewportHostProps extends PropsWithChildren {
  source: DocumentSource;
  activePage: number;
  onActivePageChange: (page: number) => void;
  onPageCountChange?: (pageCount: number) => void;
  camera?: ViewportCamera;
  onCameraChange?: Dispatch<SetStateAction<ViewportCamera>>;
  /**
   * Controls who owns drag/click interactions over the document surface.
   * `viewport` enables the host drag-pan surface.
   * `tool` disables default host drag-pan so in-viewport overlays can own pointer input.
   *   Holding Space still enables host pan above tool overlays.
   * `external` disables host drag-pan and Space-pan because a parent stage owns interactions.
   */
  pointerOwner?: "viewport" | "tool" | "external";
  enableWheelZoom?: boolean;
  viewportPadding?: Partial<ViewportPadding>;
  /**
   * Optional padding used only for pan clamping. This lets an overlaid UI reserve
   * drag room without resizing/recentering the rendered viewport.
   */
  panBoundaryPadding?: Partial<ViewportPadding>;
  className?: string;
  toolbar?: ReactNode;
  leftRail?: ReactNode;
  footer?: ReactNode;
  /**
   * Shows a collapsible page-thumbnail sidebar for PDF sources (defaults to
   * `true`). Automatically suppressed when a `leftRail` is provided so the two
   * rails never overlap.
   */
  pageSidebar?: boolean;
  /** Labels shown with page numbers in the thumbnail sidebar and pager. */
  pageLabels?: ReadonlyMap<number, string>;
}

interface ViewportRendererProps {
  source: DocumentSource;
  activePage: number;
  baseWidth: number;
  baseHeight: number;
  zoom: number;
  activePageMetrics: PageMetrics | null;
  onPageCountChange: (pageCount: number) => void;
  onPageMetricsChange: (metrics: PageMetrics) => void;
  onPageStatusChange: (
    pageNumber: number,
    status: ViewportRenderState,
    errorMessage?: string
  ) => void;
  onPdfRenderCommit: () => void;
  onImageRenderCommit: () => void;
}

const ViewportRenderer = memo(function ViewportRenderer({
  source,
  activePage,
  baseWidth,
  baseHeight,
  zoom,
  activePageMetrics,
  onPageCountChange,
  onPageMetricsChange,
  onPageStatusChange,
  onPdfRenderCommit,
  onImageRenderCommit,
}: ViewportRendererProps) {
  if (source.mediaKind === "pdf") {
    return (
      <PdfViewport
        source={source}
        activePage={activePage}
        baseWidth={baseWidth}
        baseHeight={baseHeight}
        zoom={zoom}
        activePageMetrics={activePageMetrics}
        onPageCountChange={onPageCountChange}
        onPageMetricsChange={onPageMetricsChange}
        onPageStatusChange={onPageStatusChange}
        onRenderCommit={onPdfRenderCommit}
      />
    );
  }

  if (source.mediaKind === "image") {
    return (
      <ImageViewport
        source={source}
        baseWidth={baseWidth}
        zoom={zoom}
        onPageCountChange={onPageCountChange}
        onPageMetricsChange={onPageMetricsChange}
        onPageStatusChange={onPageStatusChange}
        onRenderCommit={onImageRenderCommit}
      />
    );
  }

  return (
    <div
      className="flex items-center justify-center rounded-lg border bg-background/80 text-sm text-muted-foreground"
      style={{ width: baseWidth, height: baseHeight }}
    >
      Unsupported file type for this viewer.
    </div>
  );
});

const DEFAULT_VIEWPORT_CAMERA: ViewportCamera = {
  zoom: 1,
  pan: { x: 0, y: 0 },
  fitMode: "fit-width",
  renderScale: 1,
};

const PAGE_SIDEBAR_STORAGE_KEY = "document-viewport-page-sidebar-open";

function getInitialPageSidebarOpen() {
  if (typeof window === "undefined") {
    return true;
  }

  try {
    return window.localStorage.getItem(PAGE_SIDEBAR_STORAGE_KEY) !== "false";
  } catch {
    return true;
  }
}

export function DocumentViewportHost({
  source,
  activePage,
  onActivePageChange,
  onPageCountChange,
  camera: controlledCamera,
  onCameraChange,
  pointerOwner = "viewport",
  enableWheelZoom = pointerOwner === "viewport",
  viewportPadding,
  panBoundaryPadding,
  className,
  toolbar,
  leftRail,
  footer,
  pageSidebar = true,
  pageLabels,
  children,
}: DocumentViewportHostProps) {
  const workspaceRef = useRef<HTMLDivElement | null>(null);
  const workspacePointerOverRef = useRef(false);
  const [spacePanActive, setSpacePanActive] = useState(false);
  const [panDragStart, setPanDragStart] = useState<{
    pointerId: number;
    pointerX: number;
    pointerY: number;
    panX: number;
    panY: number;
  } | null>(null);
  const [workspaceSize, setWorkspaceSize] = useState<ViewportSize>({
    width: 1200,
    height: 780,
  });
  // Unknown until the renderer reports it for this source, so a requested page
  // is not clamped against the placeholder count of 1 before the document loads.
  const [reportedPageCount, setReportedPageCount] = useState<{
    revisionId: string;
    pageCount: number;
  } | null>(null);
  const loadedPageCount =
    reportedPageCount?.revisionId === source.revisionId ? reportedPageCount.pageCount : null;
  const pageCount = loadedPageCount ?? 1;
  const handleRendererPageCount = useCallback(
    (count: number) => setReportedPageCount({ revisionId: source.revisionId, pageCount: count }),
    [source.revisionId]
  );
  const [pageMetricsByNumber, setPageMetricsByNumber] = useState<Record<number, PageMetrics>>({});
  const [internalCamera, setInternalCamera] = useState<ViewportCamera>(DEFAULT_VIEWPORT_CAMERA);
  const camera = controlledCamera ?? internalCamera;
  const setCamera = useCallback(
    (nextCamera: SetStateAction<ViewportCamera>) => {
      if (onCameraChange) {
        onCameraChange(nextCamera);
        return;
      }
      setInternalCamera(nextCamera);
    },
    [onCameraChange]
  );
  const [renderRevision, setRenderRevision] = useState(0);
  const [pageStatus, setPageStatus] = useState<
    Record<number, { pageNumber: number; status: ViewportRenderState; errorMessage?: string }>
  >({});

  const resolvedPadding = useMemo(
    () => getDefaultViewportPadding(viewportPadding),
    [viewportPadding]
  );
  const [pageSidebarOpen, setPageSidebarOpenState] = useState(getInitialPageSidebarOpen);
  const setPageSidebarOpen = useCallback((open: boolean) => {
    setPageSidebarOpenState(open);
    try {
      window.localStorage.setItem(PAGE_SIDEBAR_STORAGE_KEY, String(open));
    } catch {
      // Persistence is best-effort; the sidebar still works without it.
    }
  }, []);
  const showPageSidebar = pageSidebar && source.mediaKind === "pdf" && !leftRail;
  // While the thumbnail sidebar is open, reserve its width so the document
  // shifts right instead of sliding underneath the panel.
  const effectiveViewportPadding = useMemo<ViewportPadding>(() => {
    if (!showPageSidebar || !pageSidebarOpen) {
      return resolvedPadding;
    }
    return {
      ...resolvedPadding,
      left: resolvedPadding.left + PAGE_SIDEBAR_LEFT_INSET,
    };
  }, [pageSidebarOpen, resolvedPadding, showPageSidebar]);
  const resolvedPanBoundaryPadding = useMemo(
    () => ({
      ...effectiveViewportPadding,
      ...panBoundaryPadding,
    }),
    [panBoundaryPadding, effectiveViewportPadding]
  );
  const pageMetrics = useMemo(
    () =>
      Object.values(pageMetricsByNumber).sort((left, right) => left.pageNumber - right.pageNumber),
    [pageMetricsByNumber]
  );
  const activePageMetrics = pageMetricsByNumber[activePage] ?? null;
  const frame = useMemo(
    () =>
      getDocumentFrame({
        workspaceSize,
        viewportPadding: effectiveViewportPadding,
        pageMetrics: activePageMetrics,
        zoom: camera.zoom,
        pan: camera.pan,
      }),
    [activePageMetrics, camera.pan, camera.zoom, effectiveViewportPadding, workspaceSize]
  );

  useEffect(() => {
    const element = workspaceRef.current;
    if (!element) {
      return;
    }

    const updateBounds = () => {
      const rect = element.getBoundingClientRect();
      setWorkspaceSize({
        width: Math.max(1, rect.width),
        height: Math.max(1, rect.height),
      });
    };

    updateBounds();

    if (typeof ResizeObserver !== "undefined") {
      const observer = new ResizeObserver(updateBounds);
      observer.observe(element);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", updateBounds);
    return () => window.removeEventListener("resize", updateBounds);
  }, []);

  useEffect(() => {
    if (loadedPageCount === null) return;
    onPageCountChange?.(loadedPageCount);
    if (activePage > loadedPageCount) {
      onActivePageChange(1);
    }
  }, [activePage, loadedPageCount, onActivePageChange, onPageCountChange]);

  useEffect(() => {
    setCamera(DEFAULT_VIEWPORT_CAMERA);
    setPageMetricsByNumber({});
    setPageStatus({});
  }, [setCamera, source.revisionId]);

  useEffect(() => {
    if (camera.fitMode !== "fit-page") {
      return;
    }

    const nextZoom = getFitPageZoom(workspaceSize, effectiveViewportPadding, activePageMetrics);
    setCamera((current) => ({ ...current, zoom: nextZoom, pan: { x: 0, y: 0 } }));
  }, [activePageMetrics, camera.fitMode, effectiveViewportPadding, setCamera, workspaceSize]);

  const requestZoomAt = useCallback(
    (targetZoom: number, anchor: ViewportPoint) => {
      setCamera((current) => {
        const nextZoom = clamp(targetZoom, 0.35, MAX_VIEWPORT_ZOOM);
        if (Math.abs(nextZoom - current.zoom) < 0.0001) {
          return current;
        }

        const currentFrame = getDocumentFrame({
          workspaceSize,
          viewportPadding: effectiveViewportPadding,
          pageMetrics: activePageMetrics,
          zoom: current.zoom,
          pan: current.pan,
        });

        const normalized = screenToPagePoint({
          point: anchor,
          frame: currentFrame,
        });

        if (!normalized) {
          return current;
        }

        const nextFrame = getDocumentFrame({
          workspaceSize,
          viewportPadding: effectiveViewportPadding,
          pageMetrics: activePageMetrics,
          zoom: nextZoom,
          pan: { x: 0, y: 0 },
        });

        const nextPan = {
          x:
            anchor.x -
            (nextFrame.centerX - nextFrame.screenWidth / 2) -
            normalized.x * nextFrame.screenWidth,
          y:
            anchor.y -
            (nextFrame.centerY - nextFrame.screenHeight / 2) -
            normalized.y * nextFrame.screenHeight,
        };

        return {
          ...current,
          zoom: nextZoom,
          pan: clampPanToDocumentBounds({
            workspaceSize,
            viewportPadding: resolvedPanBoundaryPadding,
            pageMetrics: activePageMetrics,
            zoom: nextZoom,
            pan: nextPan,
          }),
          fitMode: "custom",
        };
      });
    },
    [activePageMetrics, effectiveViewportPadding, resolvedPanBoundaryPadding, setCamera, workspaceSize]
  );

  const scaleZoomAt = useCallback(
    (multiplier: number, anchor: ViewportPoint) => {
      setCamera((current) => {
        const nextZoom = clamp(current.zoom * multiplier, 0.35, MAX_VIEWPORT_ZOOM);
        if (Math.abs(nextZoom - current.zoom) < 0.0001) {
          return current;
        }

        const currentFrame = getDocumentFrame({
          workspaceSize,
          viewportPadding: effectiveViewportPadding,
          pageMetrics: activePageMetrics,
          zoom: current.zoom,
          pan: current.pan,
        });

        const normalized = screenToPagePoint({
          point: anchor,
          frame: currentFrame,
        });

        if (!normalized) {
          return current;
        }

        const nextFrame = getDocumentFrame({
          workspaceSize,
          viewportPadding: effectiveViewportPadding,
          pageMetrics: activePageMetrics,
          zoom: nextZoom,
          pan: { x: 0, y: 0 },
        });

        return {
          ...current,
          zoom: nextZoom,
          pan: clampPanToDocumentBounds({
            workspaceSize,
            viewportPadding: resolvedPanBoundaryPadding,
            pageMetrics: activePageMetrics,
            zoom: nextZoom,
            pan: {
              x:
                anchor.x -
                (nextFrame.centerX - nextFrame.screenWidth / 2) -
                normalized.x * nextFrame.screenWidth,
              y:
                anchor.y -
                (nextFrame.centerY - nextFrame.screenHeight / 2) -
                normalized.y * nextFrame.screenHeight,
            },
          }),
          fitMode: "custom",
        };
      });
    },
    [activePageMetrics, effectiveViewportPadding, resolvedPanBoundaryPadding, setCamera, workspaceSize]
  );

  useEffect(() => {
    const workspace = workspaceRef.current;
    if (!workspace) {
      return;
    }

    const handleWheel = (event: WheelEvent) => {
      if (!enableWheelZoom) {
        return;
      }

      const rect = workspace.getBoundingClientRect();
      const pointer = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      const insideDocumentBounds =
        pointer.x >= frame.originX &&
        pointer.x <= frame.originX + frame.screenWidth &&
        pointer.y >= frame.originY &&
        pointer.y <= frame.originY + frame.screenHeight;

      if (!insideDocumentBounds) {
        return;
      }

      const dominantDelta =
        Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;
      if (dominantDelta === 0) {
        return;
      }

      event.preventDefault();
      scaleZoomAt(Math.exp(-dominantDelta * 0.0025), pointer);
    };

    workspace.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      workspace.removeEventListener("wheel", handleWheel);
    };
  }, [
    frame.originX,
    frame.originY,
    frame.screenHeight,
    frame.screenWidth,
    enableWheelZoom,
    scaleZoomAt,
  ]);

  const fitWidth = useCallback(() => {
    setCamera((current) => ({
      ...current,
      zoom: 1,
      pan: { x: 0, y: 0 },
      fitMode: "fit-width",
    }));
  }, [setCamera]);

  const fitPage = useCallback(() => {
    setCamera((current) => ({
      ...current,
      zoom: getFitPageZoom(workspaceSize, effectiveViewportPadding, activePageMetrics),
      pan: { x: 0, y: 0 },
      fitMode: "fit-page",
    }));
  }, [activePageMetrics, effectiveViewportPadding, setCamera, workspaceSize]);

  const goToPage = useCallback(
    (page: number) => {
      onActivePageChange(clamp(page, 1, pageCount));
    },
    [onActivePageChange, pageCount]
  );

  const setPan = useCallback(
    (nextPan: ViewportPoint | ((current: ViewportPoint) => ViewportPoint)) => {
      setCamera((current) => ({
        ...current,
        fitMode: "custom",
        pan: clampPanToDocumentBounds({
          workspaceSize,
          viewportPadding: resolvedPanBoundaryPadding,
          pageMetrics: activePageMetrics,
          zoom: current.zoom,
          pan: typeof nextPan === "function" ? nextPan(current.pan) : nextPan,
        }),
      }));
    },
    [activePageMetrics, resolvedPanBoundaryPadding, setCamera, workspaceSize]
  );

  // Space temporarily claims pan even when a tool owns normal drag input.
  // External owners (e.g. rev-collab) keep full control including Space.
  const hostHandlesSpacePan = pointerOwner !== "external";

  useEffect(() => {
    if (!hostHandlesSpacePan) {
      setSpacePanActive(false);
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        !isSpaceKey(event) ||
        shouldIgnoreSpacePanTarget(event.target) ||
        !isSpacePanContextActive(workspaceRef.current, workspacePointerOverRef.current)
      ) {
        return;
      }
      event.preventDefault();
      setSpacePanActive(true);
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      if (isSpaceKey(event)) {
        setSpacePanActive(false);
      }
    };

    const handleWindowBlur = () => {
      setSpacePanActive(false);
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleWindowBlur);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, [hostHandlesSpacePan]);

  useEffect(() => {
    if (!panDragStart) {
      return;
    }

    const getWorkspaceRelativePoint = (clientX: number, clientY: number) => {
      const workspace = workspaceRef.current;
      if (!workspace) {
        return null;
      }

      const rect = workspace.getBoundingClientRect();
      return {
        x: clientX - rect.left,
        y: clientY - rect.top,
      };
    };

    const handleWindowPointerMove = (event: PointerEvent) => {
      if (event.pointerId !== panDragStart.pointerId) {
        return;
      }

      const pointer = getWorkspaceRelativePoint(event.clientX, event.clientY);
      if (!pointer) {
        return;
      }

      setPan({
        x: panDragStart.panX + (pointer.x - panDragStart.pointerX),
        y: panDragStart.panY + (pointer.y - panDragStart.pointerY),
      });
    };

    const handleWindowPointerUp = (event: PointerEvent) => {
      if (event.pointerId === panDragStart.pointerId) {
        setPanDragStart(null);
      }
    };

    window.addEventListener("pointermove", handleWindowPointerMove);
    window.addEventListener("pointerup", handleWindowPointerUp);
    window.addEventListener("pointercancel", handleWindowPointerUp);

    return () => {
      window.removeEventListener("pointermove", handleWindowPointerMove);
      window.removeEventListener("pointerup", handleWindowPointerUp);
      window.removeEventListener("pointercancel", handleWindowPointerUp);
    };
  }, [panDragStart, setPan]);

  const beginPanDrag = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const workspace = workspaceRef.current;
      if (!event.isPrimary || event.button !== 0 || !workspace) {
        return;
      }

      const rect = workspace.getBoundingClientRect();
      setPanDragStart({
        pointerId: event.pointerId,
        pointerX: event.clientX - rect.left,
        pointerY: event.clientY - rect.top,
        panX: camera.pan.x,
        panY: camera.pan.y,
      });
      event.preventDefault();
    },
    [camera.pan.x, camera.pan.y]
  );

  const setPageMetrics = useCallback((metrics: PageMetrics) => {
    setPageMetricsByNumber((current) => {
      const existing = current[metrics.pageNumber];
      if (
        existing &&
        existing.width === metrics.width &&
        existing.height === metrics.height &&
        existing.rotation === metrics.rotation
      ) {
        return current;
      }
      return {
        ...current,
        [metrics.pageNumber]: metrics,
      };
    });
  }, []);

  const setPageRenderStatus = useCallback(
    (pageNumber: number, status: ViewportRenderState, errorMessage?: string) => {
      setPageStatus((current) => ({
        ...current,
        [pageNumber]: {
          pageNumber,
          status,
          errorMessage,
        },
      }));
    },
    []
  );

  const handlePdfRenderCommit = useCallback(() => {
    setRenderRevision((current) => current + 1);
    setCamera((current) => ({
      ...current,
      renderScale: getRenderScaleForZoom(current.zoom),
    }));
  }, [setCamera]);

  const handleImageRenderCommit = useCallback(() => {
    setRenderRevision((current) => current + 1);
    setCamera((current) => ({
      ...current,
      renderScale: 1,
    }));
  }, [setCamera]);

  const transforms = useMemo(
    () => ({
      screenToPagePoint: (point: ViewportPoint) => {
        const result = screenToPagePoint({ point, frame });
        if (!result) {
          return null;
        }
        return { x: result.x, y: result.y };
      },
      pageToScreenPoint: (point: ViewportPoint) => pageToScreenPoint({ point, frame }),
      screenRectToPageRect: (rect: ViewportRect) => screenRectToPageRect({ rect, frame }),
      pageRectToScreenRect: (rect: ViewportRect) => pageRectToScreenRect({ rect, frame }),
    }),
    [frame]
  );

  const contextValue = useMemo(
    () => ({
      source,
      activePage,
      pageCount,
      pageLabels,
      pageMetrics,
      activePageMetrics,
      camera,
      rotation: 0,
      renderRevision,
      isRendering: Object.values(pageStatus).some(({ status }) => status === "loading" || status === "rendering"),
      pageStatus,
      workspaceSize,
      viewportPadding: effectiveViewportPadding,
      transforms,
      requestZoomAt,
      scaleZoomAt,
      fitWidth,
      fitPage,
      goToPage,
      setPan,
    }),
    [
      activePage,
      activePageMetrics,
      camera,
      fitPage,
      fitWidth,
      goToPage,
      pageCount,
      pageLabels,
      pageMetrics,
      pageStatus,
      renderRevision,
      requestZoomAt,
      scaleZoomAt,
      effectiveViewportPadding,
      setPan,
      source,
      transforms,
      workspaceSize,
    ]
  );

  return (
    <DocumentViewportContext.Provider value={contextValue}>
      <div
        ref={workspaceRef}
        className={className}
        data-testid="document-viewport-workspace"
        onPointerEnter={() => {
          workspacePointerOverRef.current = true;
        }}
        onPointerLeave={() => {
          workspacePointerOverRef.current = false;
        }}
      >
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, hsl(var(--border) / 0.5) 1px, transparent 0)",
            backgroundSize: "20px 20px",
          }}
        />

        <div
          className="absolute"
          style={{
            left: frame.originX,
            top: frame.originY,
          }}
        >
          <ViewportRenderer
            source={source}
            activePage={activePage}
            baseWidth={frame.baseWidth}
            baseHeight={frame.baseHeight}
            zoom={camera.zoom}
            activePageMetrics={activePageMetrics}
            onPageCountChange={handleRendererPageCount}
            onPageMetricsChange={setPageMetrics}
            onPageStatusChange={setPageRenderStatus}
            onPdfRenderCommit={handlePdfRenderCommit}
            onImageRenderCommit={handleImageRenderCommit}
          />
        </div>

        {toolbar}
        {leftRail}
        {showPageSidebar ? (
          <DocumentViewportPageSidebar
            open={pageSidebarOpen}
            onOpenChange={setPageSidebarOpen}
          />
        ) : null}
        {children}
        {pointerOwner === "viewport" && !spacePanActive ? (
          <div
            className="absolute z-20"
            data-testid="document-viewport-surface"
            style={{
              left: frame.originX,
              top: frame.originY,
              width: frame.screenWidth,
              height: frame.screenHeight,
              cursor: panDragStart ? "grabbing" : "grab",
              touchAction: "none",
              userSelect: "none",
            }}
            onPointerDown={beginPanDrag}
          />
        ) : null}
        {hostHandlesSpacePan && spacePanActive ? (
          <div
            className="absolute inset-0 z-[60]"
            data-testid="document-viewport-space-pan-surface"
            style={{
              cursor: panDragStart ? "grabbing" : "grab",
              touchAction: "none",
              userSelect: "none",
            }}
            onPointerDown={beginPanDrag}
          />
        ) : null}
        {footer}
      </div>
    </DocumentViewportContext.Provider>
  );
}
